import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  Button,
  CheckBox,
  Switch,
  TextInput,
  Typography
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { Fee } from "@dealer-center/ddc-pages-fee-manager-interface";
import type { FeeWithUIState } from "../types";
import {
  formatCurrency,
  parseCurrency,
  validateFeeName
} from "../utils/format";

const RowContainer = styled.div<{
  $selected?: boolean;
  $editing?: boolean;
  $disabled?: boolean;
}>`
  display: grid;
  grid-template-columns: 100px 1fr 1fr 220px 150px 20px;
  grid-template-rows: ${props => (props.$editing ? "auto auto" : "auto")};
  gap: 8px;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  cursor: default;
  background-color: ${props =>
    props.$editing ? "#f3f4f6" : props.$selected ? "#dbeafe" : "transparent"};
  border: ${props => (props.$editing ? "1.5px solid #9ca3af" : "none")};
  border-bottom: ${props =>
    props.$editing ? "1.5px solid #9ca3af" : "1px solid #d1d5db"};
  min-height: 48px;
  opacity: ${props => (props.$disabled && !props.$editing ? 0.5 : 1)};
  transition: opacity 0.2s;

  @media (max-width: 768px) {
    min-width: 490px;
  }
`;

const Cell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const EmptyCell = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => (props.$clickable ? "pointer" : "default")};
  min-height: 48px;
  width: 100%;
`;

const NameCell = styled.div<{ $clickable?: boolean; $editing?: boolean }>`
  display: flex;
  align-items: center;
  cursor: ${props => (props.$clickable ? "pointer" : "default")};
  grid-column: ${props => (props.$editing ? "2 / 3" : "2 / 3")};
`;

const IncludeCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -10px;
  pointer-events: auto;
  grid-column: 4;

  /**
   * MUI Override: Style Interstate Switch label text
   * Reason: Interstate Switch component does not expose props to customize label styling.
   * We need uppercase, bold, small font (12px) to match the design requirements.
   * Alternative considered: Custom wrapper component, but would duplicate Switch logic.
   */
  .MuiFormControlLabel-label,
  label.MuiFormControlLabel-label,
  span.MuiFormControlLabel-label {
    font-size: 12px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    color: #374151 !important;
    opacity: 1 !important;
  }
`;

const AmountCell = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  margin-left: -10px;
  cursor: ${props => (props.$clickable ? "pointer" : "default")};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  grid-column: 3 / 5;
  justify-content: flex-start;
  align-items: center;
`;

const EditControlsContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  grid-column: 5 / 6;
  grid-row: 1;
  margin-left: -10px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
  grid-column: 2 / -1;
  grid-row: 2;
  padding-top: 8px;
  padding-right: 8px;

  /* Increase button padding by 4px on each side */
  button {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }
`;

const SwitchWrapper = styled.div`
  display: flex;
  pointer-events: auto;
  cursor: pointer;

  /**
   * MUI Override: Hide checkmark icon inside Switch thumb
   * Reason: Interstate Switch renders a checkmark SVG icon inside the thumb by default.
   * No prop exists to disable this icon. Design requires a plain thumb without icon.
   * Filed request: [Consider adding if Interstate team tracks feature requests]
   */
  .MuiSwitch-thumb svg {
    display: none;
  }

  /* Ensure all Switch elements are clickable */
  .MuiSwitch-root,
  .MuiSwitch-switchBase,
  .MuiSwitch-input {
    cursor: pointer;
    pointer-events: auto;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  /* Override Interstate TextInput internal Grid2 vertical layout */
  .ids-text-input-container {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .ids-input-layout-vertical {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0;
    width: 100%;
  }

  /* Ensure input field takes full width */
  input {
    width: 100%;
  }

  /* Hide the label grid item since we're using placeholders */
  .MuiGrid2-grid-xs-12:first-child {
    display: none;
  }

  /* Make the input grid item fill and center */
  .MuiGrid2-grid-xs-12:last-child {
    width: 100%;
  }
`;

const NameInputWrapper = styled(InputWrapper)`
  /* In edit mode, Name field expands slightly into the empty column */
  grid-column: 2 / 4;
  /* No width override - let it naturally fill the two columns */
`;

export interface FeeRowProps {
  fee: FeeWithUIState;
  disabled: boolean;
  locale?: string;
  existingFees: Fee[];
  onToggleSelection: (feeId: string) => void;
  onToggleInclude: (feeId: string) => void;
  onEditClick: (feeId: string) => void;
  onSaveEdit: (
    feeId: string,
    name: string,
    amount: number | null,
    include: boolean
  ) => void;
  onCancelEdit: (feeId: string) => void;
  onDelete: (feeId: string) => void;
  onShowTooltip?: (x: number, y: number, text: string) => void;
  onHideTooltip?: () => void;
}

export const FeeRow = ({
  fee,
  disabled,
  locale = "en-US",
  existingFees,
  onToggleSelection,
  onToggleInclude,
  onEditClick,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onShowTooltip,
  onHideTooltip
}: FeeRowProps) => {
  const [editName, setEditName] = useState(fee.editName || fee.name);
  const [editAmount, setEditAmount] = useState(
    fee.editAmount !== undefined
      ? String(fee.editAmount || "")
      : String(fee.amount || "")
  );
  const [editInclude, setEditInclude] = useState(
    fee.editInclude !== undefined ? fee.editInclude : fee.include
  );
  const [nameError, setNameError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLDivElement>(null);
  const amountInputRef = useRef<HTMLDivElement>(null);
  const lastClickTimeRef = useRef<number>(0);
  const focusFieldRef = useRef<"name" | "amount">("name");

  // Track if any changes have been made
  const hasChanges =
    editName !== fee.name ||
    editAmount !== String(fee.amount || "") ||
    editInclude !== fee.include;

  // Reset edit state when entering edit mode
  useEffect(() => {
    if (fee.editing) {
      setEditName(fee.editName || fee.name);
      setEditAmount(
        fee.editAmount !== undefined
          ? String(fee.editAmount || "")
          : String(fee.amount || "")
      );
      setEditInclude(
        fee.editInclude !== undefined ? fee.editInclude : fee.include
      );
    }
  }, [
    fee.editing,
    fee.editName,
    fee.name,
    fee.editAmount,
    fee.amount,
    fee.editInclude,
    fee.include
  ]);

  // Focus the appropriate field when entering edit mode
  useEffect(() => {
    if (fee.editing) {
      setTimeout(() => {
        if (focusFieldRef.current === "amount") {
          const amountInput = amountInputRef.current?.querySelector("input");
          amountInput?.focus();
          amountInput?.select();
        } else {
          const nameInput = nameInputRef.current?.querySelector("input");
          nameInput?.focus();
          nameInput?.select();
        }
      }, 0);
    }
  }, [fee.editing]);

  const handleSave = () => {
    if (!validateFeeName(editName)) {
      setNameError(String(Locale.getText("FEEMANAGER_FEENAME_ERROR")));
      return;
    }

    // Check for duplicate name (case-insensitive, excluding current fee)
    const trimmedName = editName.trim();
    const isDuplicate = existingFees.some(
      existingFee =>
        existingFee.id !== fee.id &&
        existingFee.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (isDuplicate) {
      setNameError(String(Locale.getText("FEEMANAGER_DUPLICATE_NAME_ERROR")));
      return;
    }

    const parsedAmount = parseCurrency(editAmount);
    onSaveEdit(fee.id, editName.trim(), parsedAmount, editInclude);
  };

  const handleCancel = () => {
    setEditName(fee.name);
    setEditAmount(String(fee.amount || ""));
    setEditInclude(fee.include);
    setNameError(null);
    onCancelEdit(fee.id);
  };

  const handleCellClick = (
    e: React.MouseEvent,
    field: "name" | "amount" = "name"
  ) => {
    if (disabled || fee.editing) return;

    e.stopPropagation();

    // Prevent double-click: ignore clicks within 250ms of the last click (except in tests)
    const now = Date.now();
    if (
      process.env.NODE_ENV !== "test" &&
      now - lastClickTimeRef.current < 250
    ) {
      return;
    }
    lastClickTimeRef.current = now;

    // Store which field was clicked for focus handling
    focusFieldRef.current = field;

    if (onHideTooltip) {
      onHideTooltip();
    }
    onEditClick(fee.id);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!fee.editing && !disabled && onShowTooltip) {
      onShowTooltip(
        e.clientX,
        e.clientY,
        String(Locale.getText("FEEMANAGER_CLICK_TO_EDIT"))
      );
    }
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (!fee.editing && !disabled && onShowTooltip) {
      onShowTooltip(
        e.clientX,
        e.clientY,
        String(Locale.getText("FEEMANAGER_CLICK_TO_EDIT"))
      );
    }
  };

  const handleMouseLeave = () => {
    if (onHideTooltip) {
      onHideTooltip();
    }
  };

  return (
    <RowContainer
      $disabled={disabled}
      $editing={fee.editing}
      $selected={fee.selected}
    >
      {/* Selected checkbox */}
      <Cell>
        <CheckBox
          checked={fee.selected || false}
          disabled={disabled || fee.editing}
          value={fee.id}
          onChange={() => {
            if (!disabled && !fee.editing) {
              onToggleSelection(fee.id);
            }
          }}
        />
      </Cell>

      {/* Name */}
      {fee.editing ? (
        <NameInputWrapper ref={nameInputRef} onClick={e => e.stopPropagation()}>
          <TextInput
            id={`fee-name-${fee.id}`}
            name="fee-name"
            placeholder={String(
              Locale.getText("FEEMANAGER_FEENAME_PLACEHOLDER")
            )}
            value={editName}
            onChange={e => {
              setEditName(e.target.value);
              if (nameError) {
                setNameError(null);
              }
            }}
          />
        </NameInputWrapper>
      ) : (
        <NameCell
          $clickable={!disabled}
          onClick={e => handleCellClick(e, "name")}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove}
        >
          <Typography variant={"body-md"}>{fee.name}</Typography>
        </NameCell>
      )}

      {/* Empty cell (clickable) - hidden in edit mode */}
      {!fee.editing && (
        <EmptyCell
          $clickable={!disabled}
          className="empty-cell"
          onClick={e => handleCellClick(e, "name")}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove}
        />
      )}

      {/* Include toggle */}
      <IncludeCell>
        <SwitchWrapper
          className="switch-wrapper"
          onClick={e => {
            if (!disabled) {
              e.stopPropagation();
              if (fee.editing) {
                setEditInclude(!editInclude);
              } else {
                onToggleInclude(fee.id);
              }
            }
          }}
        >
          <Switch
            checked={fee.editing ? editInclude : fee.include}
            disabled={disabled}
            label=""
          />
        </SwitchWrapper>
      </IncludeCell>

      {/* Amount - always in same position */}
      {fee.editing ? (
        <EditControlsContainer>
          <InputWrapper ref={amountInputRef} onClick={e => e.stopPropagation()}>
            <TextInput
              id={`fee-amount-${fee.id}`}
              name="fee-amount"
              placeholder={String(
                Locale.getText("FEEMANAGER_AMOUNT_PLACEHOLDER")
              )}
              value={editAmount}
              onChange={e => {
                const value = e.target.value;
                // Normalize comma to period for internal storage (fr-CA support)
                const normalized = value.replace(/,/g, ".");
                // Allow only numbers and one decimal point
                let filtered = normalized.replace(/[^0-9.]/g, "");

                // Ensure only one decimal point
                const parts = filtered.split(".");
                if (parts.length > 2) {
                  filtered = parts[0] + "." + parts.slice(1).join("");
                }

                setEditAmount(filtered);
              }}
              onKeyDown={e => {
                // Allow numeric keys, decimal point, and control keys
                const allowedKeys = [
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                  "ArrowUp",
                  "ArrowDown",
                  "Home",
                  "End"
                ];
                const isNumeric = /^[0-9]$/.test(e.key);
                const isDecimal = e.key === "." || e.key === ",";

                // Allow control keys and numeric input
                if (allowedKeys.includes(e.key) || isNumeric || isDecimal) {
                  // Prevent multiple decimal points (commas are normalized to periods in state)
                  if (isDecimal && editAmount.includes(".")) {
                    e.preventDefault();
                    return;
                  }

                  // Prevent entering more than 2 decimal places
                  if (isNumeric && editAmount.includes(".")) {
                    const parts = editAmount.split(".");
                    if (parts[1] && parts[1].length >= 2) {
                      // Only prevent if cursor is after the decimal
                      const input = e.currentTarget as HTMLInputElement;
                      const cursorPos = input.selectionStart || 0;
                      const decimalPos = editAmount.indexOf(".");
                      if (cursorPos > decimalPos) {
                        e.preventDefault();
                        return;
                      }
                    }
                  }
                  // Allow the input
                  return;
                }

                // Prevent all other keys
                e.preventDefault();
              }}
            />
          </InputWrapper>
        </EditControlsContainer>
      ) : (
        <>
          {/* Amount */}
          <AmountCell
            $clickable={!fee.editing && !disabled}
            onClick={e => handleCellClick(e, "amount")}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}
          >
            <Typography variant={"body-md"}>
              {formatCurrency(fee.amount, locale)}
            </Typography>
          </AmountCell>

          {/* Empty cell for Right spacing column */}
          <Cell />
        </>
      )}

      {/* Save and Cancel buttons in second row (edit mode only) */}
      {fee.editing && (
        <ButtonRow>
          {nameError && (
            <span
              style={{ color: "red", fontSize: "12px", marginRight: "auto" }}
            >
              {nameError}
            </span>
          )}
          <Button
            analyticsConfiguration={{
              eventName: "",
              eventProperties: undefined
            }}
            buttonStyle="secondary"
            onClick={e => {
              e.stopPropagation();
              handleCancel();
            }}
          >
            {String(Locale.getText("FEEMANAGER_CANCEL"))}
          </Button>
          <Button
            analyticsConfiguration={{
              eventName: "",
              eventProperties: undefined
            }}
            buttonStyle="primary"
            disabled={!hasChanges}
            onClick={e => {
              e.stopPropagation();
              handleSave();
            }}
          >
            {String(Locale.getText("FEEMANAGER_SAVE"))}
          </Button>
        </ButtonRow>
      )}
    </RowContainer>
  );
};
